import React, { useState } from 'react';
import { Button, Tag, Select, Tooltip } from 'antd';
import { DownOutlined, RightOutlined, EditOutlined, MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Brand } from '../../../../../types/brand';

interface ParentCustomFieldTreeItemProps {
  value: any;
  depth: number;
  collapsed?: boolean;
  disabled?: boolean;
  brands: Brand[];
  brandOptions: { label: string; value: number }[];
  isDraggable?: boolean;
  dragHandleProps?: any;
  isDragging?: boolean;
  hasPendingOrderChanges?: boolean;
  onCollapse?: () => void;
  onEdit?: () => void;
  onBrandChange?: (customFieldId: number, brandIds: number[]) => void;
  onBrandExpansionChange?: (expanded: boolean) => void;
}

const ParentCustomFieldTreeItem: React.FC<ParentCustomFieldTreeItemProps> = ({
  value,
  depth,
  collapsed,
  disabled,
  brands,
  brandOptions,
  isDraggable = false,
  dragHandleProps,
  isDragging = false,
  hasPendingOrderChanges = false,
  onCollapse,
  onEdit,
  onBrandChange,
  onBrandExpansionChange,
}) => {
  const { t } = useTranslation();
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [omittedValuesCount, setOmittedValuesCount] = useState<number>(0);

  const indentationWidth = 20;

  const handleBrandAdd = (brandIds: number[]) => {
    if (!onBrandChange) return;
    onBrandChange(value.id, brandIds);
  };

  return (
    <li
      className={classNames('cfs__tree-row-wrapper', {
        disabled,
        'cfs__group-header': depth === 0,
      })}
      style={
        {
          '--spacing': `${indentationWidth * depth}px`,
          display: 'flex',
          alignItems: 'center',
          minHeight: '54px',
        } as React.CSSProperties
      }
    >
      <div className="cfs__tree-item flex-1">
        {isDraggable && (
          <div
            {...dragHandleProps}
            className="parent-custom-field-drag-handle"
            style={{
              marginRight: 8,
              opacity: isDragging ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <MenuOutlined style={{ fontSize: depth === 0 ? 14 : 12 }} />
          </div>
        )}

        {onCollapse && (
          <Button
            type="text"
            onClick={onCollapse}
            icon={collapsed ? <RightOutlined /> : <DownOutlined />}
            style={{ marginRight: 8, minWidth: 24 }}
          />
        )}

        <span
          className="cfs__text"
          style={{
            fontWeight: depth === 0 ? 600 : 400,
            marginRight: 'auto',
            minWidth: 200,
          }}
        >
          {value.name}
        </span>

        {depth > 0 && (
          <div className="flex flex-1 items-center">
            {!disabled && !!onBrandChange && (
              <Select
                mode="multiple"
                style={{ flex: 1, minWidth: 300 }}
                placeholder={t('settings:customFields.selectBrandsToAdd')}
                value={value.brandIds}
                disabled={hasPendingOrderChanges}
                showSearch
                filterOption={(input, option) => {
                  return !!option && option.label.toLowerCase().includes(input.toLowerCase());
                }}
                onChange={(value: number[]) => {
                  handleBrandAdd(value);
                }}
                getPopupContainer={trigger => trigger.parentElement || document.body}
                popupRender={menu => (
                  <div>
                    {menu}
                    <div
                      style={{
                        display: 'flex',
                        marginBottom: '4px',
                        marginTop: '4px',
                      }}
                    >
                      <Button
                        type="default"
                        disabled={hasPendingOrderChanges}
                        onClick={() => {
                          handleBrandAdd(brands.map(b => b.id));
                        }}
                      >
                        {t('common:selectAll')} ({brands.length})
                      </Button>
                      <Button
                        className="ml-2"
                        type="default"
                        disabled={hasPendingOrderChanges}
                        onClick={() => {
                          handleBrandAdd([]);
                        }}
                      >
                        {t('common:deselectAll')}
                      </Button>
                    </div>
                  </div>
                )}
                options={brandOptions}
                maxTagPlaceholder={omittedValues => {
                  if (omittedValuesCount !== omittedValues.length)
                    setOmittedValuesCount(omittedValues.length);
                  return (
                    <Tooltip
                      styles={{ body: { width: '400px' } }}
                      placement="topRight"
                      title={omittedValues.map(({ label, value }) => (
                        <Tag key={value} className="mb-1">
                          {label}
                        </Tag>
                      ))}
                      destroyOnHidden
                    >
                      <span>+{omittedValues.length}...</span>
                    </Tooltip>
                  );
                }}
                maxTagCount={!showAllBrands ? 7 : undefined}
                allowClear
              />
            )}

            <div style={{ width: '110px' }}>
              {!showAllBrands && omittedValuesCount > 0 && (
                <Tooltip
                  title={
                    hasPendingOrderChanges
                      ? t('settings:customFields.saveOrderChangesFirst')
                      : undefined
                  }
                >
                  <Button
                    type="link"
                    disabled={hasPendingOrderChanges}
                    onClick={() => {
                      setShowAllBrands(true);
                      if (onBrandExpansionChange) {
                        onBrandExpansionChange(true);
                      }
                    }}
                  >
                    {t('settings:customFields.more')}
                  </Button>
                </Tooltip>
              )}
              {showAllBrands && (
                <Tooltip
                  title={
                    hasPendingOrderChanges
                      ? t('settings:customFields.saveOrderChangesFirst')
                      : undefined
                  }
                >
                  <Button
                    type="link"
                    disabled={hasPendingOrderChanges}
                    onClick={() => {
                      setShowAllBrands(false);
                      if (onBrandExpansionChange) {
                        onBrandExpansionChange(false);
                      }
                    }}
                  >
                    {t('settings:customFields.less')}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        <Button
          className="ml-2"
          type="default"
          onClick={onEdit}
          icon={<EditOutlined />}
          disabled={value.id === 'other'}
        >
          {t('common:edit')}
        </Button>
      </div>
    </li>
  );
};

export default React.memo(ParentCustomFieldTreeItem);
